import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaTimes, FaExchangeAlt, FaBarcode, FaQrcode, FaCheck, FaBoxOpen } from 'react-icons/fa';
import OutboundBarcodeScanner from './OutboundBarcodeScanner';
import api from '../../utils/api';
import Loader from '../ui/Loader';
import { fetchWarehouses } from '../../actions/inventoryActions';

const TransferModal = ({ batch, fromWarehouseId, fromWarehouseName, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    to_warehouse_id: '',
    notes: ''
  });
  const [transferMethod, setTransferMethod] = useState('scan_selection'); // 'scan_selection' | 'select_all' | 'manual_selection'
  const [scannedCodes, setScannedCodes] = useState([]);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user } = useSelector(state => state.auth);
  const { warehouses, loading: warehousesLoading } = useSelector(state => state.warehouseList);
  const targetWarehouses = warehouses.filter(w => w.id !== fromWarehouseId);
  const targetWarehouse = targetWarehouses.find(w => w.id === formData.to_warehouse_id);
  const dispatch = useDispatch();

  useEffect(() => {
    // 获取仓库列表
    dispatch(fetchWarehouses());
  }, [dispatch]);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBarcodeScanned = (code) => {
    if (!scannedCodes.includes(code)) {
      setScannedCodes(prev => [...prev, code]);
    }
    setShowBarcodeScanner(false);
  };

  const removeScannedCode = (code) => {
    setScannedCodes(prev => prev.filter(c => c !== code));
  };

  const getTransferQuantity = () => {
    if (transferMethod === 'select_all') {
      return batch?.remaining_units || 0; // 添加空值检查
    } else if (transferMethod === 'scan_selection') {
      return scannedCodes.length;
    } 
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 添加调试信息
    console.log('Transfer Debug Info:', {
      batch: batch,
      batch_id: batch?.batch_id,
      fromWarehouseId: fromWarehouseId,
      to_warehouse_id: formData.to_warehouse_id,
      user: user,
      operator: user?.full_name || user?.phone_number || ''
    });
    
    // 验证 batch 对象
    if (!batch || !batch.batch_id) {
      setError('批次信息无效，请刷新页面重试');
      console.error('Invalid batch object:', batch);
      return;
    }
    
    // 验证源仓库ID
    if (!fromWarehouseId) {
      setError('源仓库信息丢失，请刷新页面重试');
      return;
    }
    
    if (!formData.to_warehouse_id) {
      setError('请选择目标仓库');
      return;
    }

    // 验证操作员信息
    const operator = user?.full_name || user?.phone_number || '';
    if (!operator) {
      setError('操作员信息缺失，请重新登录');
      return;
    }

    const quantity = getTransferQuantity();
    if (quantity === 0) {
      setError('请选择要调拨的货物');
      return;
    }

    if (quantity > batch.remaining_units) {
      setError(`调拨数量不能超过剩余库存 (${batch.remaining_units} 瓶)`);
      return;
    }

    setLoading(true);
    
    try {
      const requestData = {
        batch_id: batch.batch_id,
        from_warehouse_id: fromWarehouseId,
        to_warehouse_id: parseInt(formData.to_warehouse_id),
        transfer_type: transferMethod,
        quantity_units: quantity,
        operator: operator,
        notes: formData.notes || ''
      };

      // 添加调试日志
      console.log('Transfer Request Data:', requestData);

      if (transferMethod === 'scan_selection') {
        requestData.scanned_codes = scannedCodes;
      }
      
      const response = await api.post('/inventory/transfer', requestData);
      console.log('Transfer Response:', response.data);
      onSuccess();
    } catch (err) {
      console.error('Transfer Error:', err);
      console.error('Error Response:', err.response?.data);
      setError(err.response?.data?.error || '调拨失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-90vh overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold flex items-center">
              <FaExchangeAlt className="mr-3" />
              货物调拨
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {/* 批次信息 */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold mb-3">批次信息</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">批次ID：</span>
                  <span className="font-medium">#{batch.batch_id}</span>
                </div>
                <div>
                  <span className="text-gray-600">来源仓库：</span>
                  <span className="font-medium">{fromWarehouseName}</span>
                </div>
                <div>
                  <span className="text-gray-600">剩余库存：</span>
                  <span className="font-medium text-blue-600">
                    {batch.remaining_units} 瓶 ({(batch.remaining_units / batch.units_per_box).toFixed(2)} 箱)
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">单位成本：</span>
                  <span className="font-medium">¥{parseFloat(batch.unit_cost).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* 目标仓库选择 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                目标仓库 *
              </label>
              {warehousesLoading ? (
                <div className="p-2">
                  <Loader size="sm" />
                </div>
              ) : (
                <select
                  name="to_warehouse_id"
                  value={formData.to_warehouse_id}
                  onChange={handleChange}
                  className="w-full p-3 border rounded-lg"
                  required
                >
                  <option value="">选择目标仓库</option>
                  {targetWarehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name} {warehouse.address && `(${warehouse.address})`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* 调拨方式选择 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                调拨方式 *
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="transferMethod"
                    value="scan_selection"
                    checked={transferMethod === 'scan_selection'}
                    onChange={(e) => setTransferMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span>扫码选择</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="transferMethod"
                    value="select_all"
                    checked={transferMethod === 'select_all'}
                    onChange={(e) => setTransferMethod(e.target.value)}
                    className="mr-2"
                  />
                  <span>全选调拨</span>
                </label>
                
              </div>
            </div>

            {/* 扫码选择 */}
            {transferMethod === 'scan_selection' && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">扫码选择</h3>
                  <button
                    type="button"
                    onClick={() => setShowBarcodeScanner(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center"
                  >
                    <FaBarcode className="mr-2" />
                    扫码
                  </button>
                </div>
                
                {scannedCodes.length > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">已扫描条码 ({scannedCodes.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {scannedCodes.map((code, index) => (
                        <div key={index} className="bg-white p-2 rounded border flex justify-between items-center">
                          <span className="font-mono text-sm">{code}</span>
                          <button
                            type="button"
                            onClick={() => removeScannedCode(code)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 全选调拨 */}
            {transferMethod === 'select_all' && (
              <div className="mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">全选调拨</h3>
                  <p className="text-gray-600">
                    将调拨该批次的所有剩余瓶子：<span className="font-semibold">{batch.remaining_units} 瓶</span>
                  </p>
                </div>
              </div>
            )}


            {/* 调拨数量预览 */}
            <div className="mb-6">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">调拨预览</h3>
                <div className="text-sm">
                  <div>调拨数量: <span className="font-semibold">{getTransferQuantity()} 瓶</span></div>
                  {targetWarehouse && (
                    <div>目标仓库: <span className="font-semibold">{targetWarehouse.name}</span></div>
                  )}
                  <div>剩余库存: <span className="font-semibold">{batch.remaining_units - getTransferQuantity()} 瓶</span></div>
                </div>
              </div>
            </div>

            {/* 备注 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                备注
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full p-3 border rounded-lg"
                placeholder="可选的调拨备注信息"
              />
            </div>

            {/* 提交按钮 */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading || getTransferQuantity() === 0}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading && <Loader size="sm" className="mr-2" />}
                确认调拨
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 扫码器模态框 */}
      {showBarcodeScanner && (
        <OutboundBarcodeScanner
          onComplete={handleBarcodeScanned}
          onClose={() => setShowBarcodeScanner(false)}
          title="扫描调拨商品条码"
        />
      )}
    </>
  );
};

export default TransferModal;